// StaysBlocksTab — pestaña superadmin para detectar y borrar bloqueos
// de Stays que quedaron coexistiendo con su reserva real en la misma
// propiedad y rango de fechas.
//
// Por qué existe (round 2026-05-30): el flujo normal de Airbnb (vía
// Stays) cuando un huésped reserva es:
//   1. Huésped consulta → webhook `reservation.created type:"blocked"`
//      crea un block tentativo con `id_reserval`, `partnerCode` HMxxx
//      y `internalNote` "Hola, [nombre]...".
//   2. Huésped confirma → DOS webhooks:
//        a. `reservation.deleted` para borrar el block tentativo.
//        b. `reservation.created type:"booked"` para crear la reserva.
//
// En algunos casos pasados el delete del tentativo nunca llegó / se
// perdió / fue procesado en orden invertido durante el cron paralizado
// (17-29 may 2026) → el block tentativo quedó en DB. Visualmente en
// /calendario se ve como "Bloqueo: Hola, ..." o "Bloqueo: HM[code]"
// coexistiendo con el booking real correspondiente.
//
// Esta pestaña detecta esos casos (criterio: id_reserval NOT NULL +
// reserva_id IS NULL + overlap con un booking real en la misma
// propiedad) y permite borrarlos.
//
// El fallback en `stays.service.js → reservation.created` ya hace el
// mismo trabajo automáticamente cuando llega un booking real fresco
// (round 2026-05-29 fase 4 + extensión 2026-05-30 al path
// "modified-create-not-found"). Esta pestaña limpia los residuos
// históricos que el fallback nunca tuvo oportunidad de borrar.
//
// Ver docs/modulo-stays.md §3.1.F y docs/modulo-calendario.md.

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
  Statistic,
  Row,
  Col,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { apiFetch } from "../../services/apiClient";
import { getModal, getMsg } from "../../utils/antdStatic";

import { paginacionCliente } from "../../utils/tablaPaginacion";
const { Text, Paragraph, Title } = Typography;

// Fila tal como la devuelve el backend (snake_case del SQL crudo —
// usamos los nombres exactos del SELECT en services/staysBlocksCoexisting.js).
interface CoexistingBlockRow {
  block_id: number;
  block_prop_id: number;
  block_prop_nombre: string | null;
  block_fecha_inicio: string | null;
  block_fecha_termino: string | null;
  block_razon: string | null;
  block_partner_code: string | null;
  block_id_reserval: string | null;
  block_creado_por_admin: boolean | null;
  booking_id: number;
  booking_fecha_ingreso: string | null;
  booking_fecha_salida: string | null;
  booking_nombre_huesped: string | null;
  booking_id_reserval: string | null;
  booking_id_reservac: string | null;
}

interface ScanResponse {
  scanned: number;
  coexisting: CoexistingBlockRow[];
}

interface DeleteResponse {
  applied: boolean;
  scanned: number;
  affected: number;
  coexisting: CoexistingBlockRow[];
}

// Columnas de la tabla. Mostramos el block (con su rango, razón y
// código Stays) junto al booking real con el que coexiste, para que
// el admin pueda verificar a simple vista que no está borrando algo
// importante.
const columns: ColumnsType<CoexistingBlockRow> = [
  {
    title: "Block ID",
    dataIndex: "block_id",
    key: "block_id",
    width: 90,
    sorter: (a, b) => a.block_id - b.block_id,
  },
  {
    title: "Propiedad",
    dataIndex: "block_prop_nombre",
    key: "block_prop_nombre",
    ellipsis: true,
    render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
  },
  {
    title: "Rango bloqueo",
    key: "block_range",
    width: 200,
    render: (_: unknown, row: CoexistingBlockRow) => (
      <Text style={{ fontSize: 12 }}>
        {row.block_fecha_inicio ?? "—"} → {row.block_fecha_termino ?? "—"}
      </Text>
    ),
  },
  {
    title: "Etiqueta",
    key: "block_label",
    ellipsis: true,
    render: (_: unknown, row: CoexistingBlockRow) => (
      <Space direction="vertical" size={0}>
        {row.block_partner_code ? (
          <Tag color="orange">{row.block_partner_code}</Tag>
        ) : null}
        <Text style={{ fontSize: 12 }} type="secondary">
          {row.block_razon ?? "—"}
        </Text>
      </Space>
    ),
  },
  {
    title: "Coexiste con reserva",
    key: "booking",
    width: 260,
    render: (_: unknown, row: CoexistingBlockRow) => (
      <Space direction="vertical" size={0}>
        <Text strong style={{ fontSize: 12 }}>
          #{row.booking_id} {row.booking_nombre_huesped ?? ""}
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {row.booking_fecha_ingreso ?? "—"} → {row.booking_fecha_salida ?? "—"}
        </Text>
      </Space>
    ),
  },
];

const StaysBlocksTab: React.FC = () => {
  const [scanned, setScanned] = useState<boolean>(false);
  const [rows, setRows] = useState<CoexistingBlockRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);

  // Scan inicial al montar — patrón del OrphanCleaningsTab. Le da al
  // admin la información cruda apenas abre la pestaña.
  const handleScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<ScanResponse>(
        "/admin/stays-blocks-coexisting",
        { method: "GET" },
      );
      if (res) {
        setRows(res.coexisting ?? []);
        setScanned(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void handleScan();
  }, [handleScan]);

  // Modal de confirmación + POST con apply:true. Replica el patrón
  // de los 4 bloques de OrphanCleaningsTab — la confirmación explícita
  // protege contra clic accidental porque la operación toca el
  // calendario que ven todos los usuarios.
  const handleApply = useCallback(() => {
    if (rows.length === 0) return;
    getModal().confirm({
      title: `¿Borrar ${rows.length} bloqueo${rows.length === 1 ? "" : "s"} de Stays?`,
      icon: <WarningOutlined style={{ color: "#fa8c16" }} />,
      content: (
        <Paragraph style={{ marginBottom: 0 }}>
          Estos son bloqueos que vienen de Stays (id_reserval poblado) y que
          coexisten con una reserva real en la misma propiedad y rango. Son
          tentativos del flujo Airbnb que nunca se borraron porque el delete
          se perdió o llegó fuera de orden. La operación es irreversible.
        </Paragraph>
      ),
      okText: "Borrar",
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelText: "Cancelar",
      onOk: async () => {
        setApplying(true);
        try {
          const res = await apiFetch<DeleteResponse>(
            "/admin/stays-blocks-coexisting",
            { method: "POST", body: JSON.stringify({ apply: true }) },
          );
          if (res?.applied) {
            getMsg().success(
              `Se eliminaron ${res.affected} bloqueo${res.affected === 1 ? "" : "s"} coexistente${res.affected === 1 ? "" : "s"}.`,
            );
            await handleScan();
          }
        } finally {
          setApplying(false);
        }
      },
    });
  }, [rows, handleScan]);

  const empty = scanned && rows.length === 0;

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        Bloqueos de Stays coexistiendo con su reserva real
      </Title>
      <Paragraph type="secondary">
        Detecta bloqueos del flujo Airbnb/Stays (tentativos cuando un huésped
        consulta) que nunca se borraron al confirmarse la reserva real. El
        criterio: <code>id_reserval IS NOT NULL</code> +{" "}
        <code>reserva_id IS NULL</code> + overlap por propiedad y fechas con
        un booking existente. Los bloqueos AUTO de salida tardía
        (<code>reserva_id IS NOT NULL</code>) y los bloqueos manuales del
        admin/cliente (<code>id_reserval IS NULL</code>) NUNCA se tocan.
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Statistic
              title="Bloqueos coexistentes detectados"
              value={rows.length}
              prefix={
                empty ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : (
                  <WarningOutlined style={{ color: "#fa8c16" }} />
                )
              }
            />
          </Col>
          <Col>
            <Space>
              <Button
                icon={<SearchOutlined />}
                onClick={handleScan}
                loading={loading}
              >
                Reescanear
              </Button>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={handleApply}
                loading={applying}
                disabled={rows.length === 0}
              >
                Borrar {rows.length > 0 ? `(${rows.length})` : ""}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {empty ? (
        <Alert
          type="success"
          showIcon
          message="Sin coexistencias"
          description="No hay bloqueos de Stays huérfanos coexistiendo con reservas reales. El calendario está limpio."
        />
      ) : (
        <Table<CoexistingBlockRow>
          columns={columns}
          dataSource={rows}
          rowKey="block_id"
          size="small"
          pagination={paginacionCliente({ porDefecto: 20, etiqueta: "bloqueo" })}
          scroll={{ x: "max-content" }}
          loading={loading}
        />
      )}
    </div>
  );
};

export default StaysBlocksTab;
